import { useRef, useState, useCallback, useEffect, useId } from "react";
import { createPortal } from "react-dom";
import { PInfo, type PInfoMedia } from "@/components/PInfo/PInfo";

/** What a site can ask of the bubble on top of the positional arguments. */
export type DelayedTooltipOptions = {
  /**
   * Let a tap open it (board 01.11., L-11 / L-26). OPT-IN: the hook hangs off
   * ~80 HoverInfo sites and most are not ⓘ buttons — delete buttons, drawer
   * chrome, an ⓘ inside a checkbox label — where a tap must keep doing what
   * it does today. Without it nothing below changes.
   *
   * With it the trigger follows SearchScopeInfo's gating: hover opens only
   * for a mouse (a finger's synthetic mouseenter is not even bound), focus
   * only when `:focus-visible` (a tap focuses the button too), a click opens
   * at once and never reaches the element around the trigger — a mouse click
   * keeps it open, a tap toggles — and a press anywhere else closes it.
   */
  tapToOpen?: boolean;
  /** A small picture above the sentence (see PInfo). */
  media?: PInfoMedia;
};

/**
 * Shows a {@link PInfo} bubble after the pointer rests on an element for `delay`
 * ms — the "hover a bit longer to learn what this does" pattern. Attach `ref`
 * and the two mouse handlers to the trigger element and render `tooltip` (a
 * portal node) anywhere in that component's JSX.
 *
 * The bubble is portalled to `document.body` so framer-motion transform
 * ancestors (which turn `position: fixed` into a local containing block) can't
 * clip or mis-anchor it.
 *
 * With `tapToOpen` the trigger binds `tap` instead of the two mouse handlers
 * (see {@link DelayedTooltipOptions}).
 */
export function useDelayedTooltip<T extends HTMLElement>(
  message?: string,
  delay = 500,
  /**
   * Hold the bubble back while this is true — e.g. the trigger's own popup is
   * open, so the "what does this do" hint would just overlap it. A pointer that
   * never leaves the box (click-to-open) would otherwise let the delay timer
   * fire on top of the open popup.
   */
  suppressed = false,
  { tapToOpen = false, media }: DelayedTooltipOptions = {},
) {
  const ref = useRef<T | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pos, setPos] = useState<{
    top: number;
    left: number;
    showAbove: boolean;
  } | null>(null);
  // The tap path's state. `pressOwner`: a TAP opened the bubble, so the
  // trigger carries `data-press-owner` (lib/a11y/dismissOwner.ts) — the press
  // that closes it is the bubble's, not the dialog's behind it.
  const [pressOwner, setPressOwner] = useState(false);
  // Which device pressed last. A tap fires pointerdown, focus and click in
  // that order; the click alone cannot tell a finger from a mouse.
  const pointerType = useRef("mouse");
  const bubbleId = useId();
  const mediaHeight = media?.height ?? 0;

  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const place = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Anchor below the trigger (control panels sit at the top of the page) —
    // unless there isn't room: a trigger near the bottom of the viewport (the
    // last section of a tall modal) would otherwise open its bubble off-
    // screen, and PInfo only clamps horizontally. Flipping mirrors
    // useParentPosition: `top` then means "distance from the viewport bottom
    // to the trigger's top", which is what PInfo's `showAbove` branch reads.
    // A picture above the sentence makes the bubble that much taller.
    const BUBBLE_ALLOWANCE = 120 + mediaHeight;
    const spaceBelow = window.innerHeight - rect.bottom;
    const showAbove = spaceBelow < BUBBLE_ALLOWANCE && rect.top > spaceBelow;
    setPos({
      top: showAbove ? window.innerHeight - rect.top : rect.bottom,
      left: rect.left,
      showAbove,
    });
  }, [mediaHeight]);

  const show = useCallback(() => {
    if (!message || suppressed) return;
    clear();
    timer.current = setTimeout(place, delay);
  }, [message, delay, clear, suppressed, place]);

  const hide = useCallback(() => {
    clear();
    setPos(null);
    if (tapToOpen) setPressOwner(false);
  }, [clear, tapToOpen]);

  // Drop any pending timer if the trigger unmounts mid-hover.
  useEffect(() => clear, [clear]);

  // WCAG 1.4.13: hoverable/focusable extra content must be dismissible
  // without moving the pointer — Escape drops a shown bubble.
  useEffect(() => {
    if (!pos) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") hide();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [pos, hide]);

  // Cancel any pending timer AND drop a shown bubble the moment suppression
  // turns on (popup opened under a resting pointer). Nulling `pos` here is what
  // stops the bubble from resurrecting once suppression lifts again.
  useEffect(() => {
    if (suppressed) {
      clear();
      setPos(null);
      if (tapToOpen) setPressOwner(false);
    }
  }, [suppressed, clear, tapToOpen]);

  // ── tap path (only ever armed with `tapToOpen`) ──

  const tapOpen = tapToOpen && pressOwner && pos !== null;

  // A tapped bubble closes on a press anywhere but the trigger and the bubble.
  // CAPTURE phase: it must see the press before any handler on the way can
  // stop it. The marker STAYS on through this press — a host reads it on the
  // way down (`hasDismissOwner`), and dropping it here would let the very
  // press that closes the bubble also close the dialog under it.
  useEffect(() => {
    if (!tapOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target instanceof Node ? e.target : null;
      if (target && ref.current?.contains(target)) return;
      if (target && document.getElementById(bubbleId)?.contains(target)) return;
      clear();
      setPos(null);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [tapOpen, bubbleId, clear]);

  // …and on a scroll that carries the trigger away (audit r1 H-L2): the
  // bubble is fixed to the viewport, and a swipe started ON the ⓘ is no press
  // elsewhere, so it stayed hanging over whatever scrolled under it. CAPTURE
  // on the document: a scroll does not bubble, and this sees every scroller's
  // and the page's own. `hide` drops the marker with it — no click follows a
  // scroll. A scroll that leaves the trigger where it was (another scroller;
  // the late event of the scroll that focusing the ⓘ may have caused before
  // the bubble was placed) keeps a bubble that is still anchored.
  useEffect(() => {
    if (!tapOpen || !pos) return;
    const onScroll = () => {
      const rect = ref.current?.getBoundingClientRect();
      if (rect) {
        const top = pos.showAbove ? window.innerHeight - rect.top : rect.bottom;
        if (top === pos.top && rect.left === pos.left) return;
      }
      hide();
    };
    document.addEventListener("scroll", onScroll, true);
    return () => document.removeEventListener("scroll", onScroll, true);
  }, [tapOpen, pos, hide]);

  // The bubble is closed but the marker still up: the press that closed it is
  // under way. The marker goes once that press's click has gone through every
  // handler (a timeout after it); a press that never clicks — a scroll ends
  // in pointercancel — or a new press drops it at once.
  const releasing = tapToOpen && pressOwner && pos === null;
  useEffect(() => {
    if (!releasing) return;
    let after: ReturnType<typeof setTimeout> | null = null;
    const release = () => setPressOwner(false);
    const onClick = () => {
      after = setTimeout(release, 0);
    };
    document.addEventListener("click", onClick, true);
    document.addEventListener("pointercancel", release, true);
    document.addEventListener("pointerdown", release, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("pointercancel", release, true);
      document.removeEventListener("pointerdown", release, true);
      if (after) clearTimeout(after);
    };
  }, [releasing]);

  /** Open now, no delay. False when there is nothing to open. */
  const openNow = useCallback(() => {
    if (!message || suppressed) return false;
    clear();
    place();
    return true;
  }, [message, suppressed, clear, place]);

  const tap = tapToOpen
    ? {
        onPointerEnter: (e: React.PointerEvent) => {
          if (e.pointerType === "mouse") show();
        },
        onPointerLeave: (e: React.PointerEvent) => {
          if (e.pointerType === "mouse") hide();
        },
        onPointerDown: (e: React.PointerEvent) => {
          pointerType.current = e.pointerType;
        },
        // A press that never clicks — a scroll started on the trigger ends in
        // pointercancel — must not leave its finger behind for the next click
        // (audit r1 H-L3).
        onPointerCancel: () => {
          pointerType.current = "mouse";
        },
        onClick: (e: React.MouseEvent) => {
          // Inside an export row this click would toggle the row's option.
          e.stopPropagation();
          clear();
          // `detail` 0: no press made this click — a keyboard's Enter or
          // Space — so it is no tap, whatever pressed the trigger last.
          const touch = e.detail !== 0 && pointerType.current !== "mouse";
          // A keyboard's Enter after a tap is no tap.
          pointerType.current = "mouse";
          if (!touch) {
            // A mouse click keeps it open (hover may have opened it already).
            openNow();
          } else if (pos) {
            hide();
          } else if (openNow()) {
            setPressOwner(true);
          }
        },
        /** `data-press-owner` while a tapped bubble is open (and its closing press runs). */
        pressOwner,
      }
    : undefined;

  // Focus: a tap focuses the button as well — only keyboard focus may open.
  // The event is optional: direct callers (BlockFrameLayer) call it bare.
  const onFocus: (e?: React.FocusEvent) => void = tapToOpen
    ? (e) => {
        if (e?.target instanceof Element && e.target.matches(":focus-visible")) show();
      }
    : show;

  // Blur: a press elsewhere blurs the button with no next element — the
  // press itself closes a TAPPED bubble (and keeps the marker through it).
  // Tabbing on, or any bubble not opened by a tap, hides as before.
  const onBlur: (e?: React.FocusEvent) => void = tapToOpen
    ? (e) => {
        if (pressOwner && !e?.relatedTarget) return;
        hide();
      }
    : hide;

  // Render gate as well as the effect: hides in the *same* commit suppression
  // turns on, with no one-frame flash waiting for the effect to run.
  const tooltip =
    !suppressed && pos && message
      ? createPortal(
          <PInfo
            message={message}
            position={{ top: pos.top, left: pos.left, showAbove: pos.showAbove }}
            id={tapToOpen ? bubbleId : undefined}
            media={media}
          />,
          document.body,
        )
      : null;

  return {
    ref,
    onMouseEnter: show,
    onMouseLeave: hide,
    // Keyboard parity: focusing the trigger reveals the hint like hover does.
    onFocus,
    onBlur,
    tooltip,
    /** The tap path's handlers; undefined without `tapToOpen`. */
    tap,
  };
}
